/**
 * Enhanced SCADA Table Component
 * Displays SCADA tags with configurable columns and existing alarm priority colors
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  FormControlLabel,
  Switch,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Menu,
  MenuItem,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Alert,
} from '@mui/material';
import {
  GetApp as ExportIcon,
  Settings as SettingsIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { visuallyHidden } from '@mui/utils';

// Types
interface SCADATag {
  id: number;
  tag_name: string;
  current_value: number;
  current_value_display: string;
  alarm_status: string;
  alarms_enabled: boolean;
  description: string;
  engineering_unit: string;
  equipment_name?: string;
  timestamp?: string;
  alarm_priority?: {
    level: number;
    name: string;
    color: string;
    description?: string;
  };
}

interface AlarmPriority {
  level: number;
  name: string;
  color: string;
  description: string;
}

interface TableColumn {
  id: keyof SCADATag | 'actions';
  label: string;
  sortable: boolean;
  visible: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
}

interface EnhancedSCADATableProps {
  tags: SCADATag[];
  loading?: boolean;
  onTagClick?: (tag: SCADATag) => void;
  onRefresh?: () => void;
  showConfiguration?: boolean;
}

// Default columns configuration
const defaultColumns: TableColumn[] = [
  { id: 'tag_name', label: 'Tag Name', sortable: true, visible: true, width: '200px' },
  { id: 'description', label: 'Description', sortable: true, visible: true, width: '300px' },
  { id: 'current_value_display', label: 'Current Value', sortable: true, visible: true, width: '150px', align: 'right' },
  { id: 'alarm_status', label: 'Alarm Status', sortable: true, visible: true, width: '150px', align: 'center' },
  { id: 'equipment_name', label: 'Equipment', sortable: true, visible: false, width: '200px' },
  { id: 'timestamp', label: 'Last Update', sortable: true, visible: false, width: '180px' },
  { id: 'actions', label: 'Actions', sortable: false, visible: true, width: '100px', align: 'center' },
];

// Alarm status colors and icons
const getAlarmStatusChip = (alarmStatus: string, priority?: AlarmPriority) => {
  const getStatusColor = () => {
    if (priority && ['HIGH_ALARM', 'LOW_ALARM', 'BOOLEAN_TRUE_ALARM', 'BOOLEAN_FALSE_ALARM'].includes(alarmStatus)) {
      return priority.color;
    }
    
    switch (alarmStatus) {
      case 'NORMAL':
      case 'ALARMS_DISABLED':
        return '#388e3c'; // Green
      case 'HIGH_ALARM':
      case 'LOW_ALARM':
      case 'BOOLEAN_TRUE_ALARM':
      case 'BOOLEAN_FALSE_ALARM':
        return '#d32f2f'; // Red (fallback)
      case 'COMMUNICATION_ERROR':
        return '#757575'; // Gray
      default:
        return '#1976d2'; // Blue
    }
  };

  const getStatusLabel = () => {
    switch (alarmStatus) {
      case 'NORMAL':
        return 'Normal';
      case 'HIGH_ALARM':
        return 'High Alarm';
      case 'LOW_ALARM':
        return 'Low Alarm';
      case 'BOOLEAN_TRUE_ALARM':
        return 'Bool True Alarm';
      case 'BOOLEAN_FALSE_ALARM':
        return 'Bool False Alarm';
      case 'ALARMS_DISABLED':
        return 'Disabled';
      case 'COMMUNICATION_ERROR':
        return 'Comm Error';
      default:
        return alarmStatus;
    }
  };

  const isAlarm = ['HIGH_ALARM', 'LOW_ALARM', 'BOOLEAN_TRUE_ALARM', 'BOOLEAN_FALSE_ALARM'].includes(alarmStatus);
  
  return (
    <Chip
      label={getStatusLabel()}
      size="small"
      sx={{
        backgroundColor: getStatusColor(),
        color: 'white',
        fontWeight: 'bold',
        '& .MuiChip-label': {
          fontSize: '0.75rem',
        },
      }}
    />
  );
};

export const EnhancedSCADATable: React.FC<EnhancedSCADATableProps> = ({
  tags,
  loading = false,
  onTagClick,
  onRefresh,
  showConfiguration = true,
}) => {
  // State
  const [columns, setColumns] = useState<TableColumn[]>(defaultColumns);
  const [orderBy, setOrderBy] = useState<keyof SCADATag>('tag_name');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [filterText, setFilterText] = useState('');
  const [alarmFilter, setAlarmFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<number[]>([]);
  const [configMenuAnchor, setConfigMenuAnchor] = useState<null | HTMLElement>(null);
  const [showValues, setShowValues] = useState(true);
  const [showDescriptions, setShowDescriptions] = useState(true);
  const [showAlarmStatus, setShowAlarmStatus] = useState(true);

  // Get unique alarm priorities from tags
  const availablePriorities = useMemo(() => {
    const priorities = new Map<number, AlarmPriority>();
    tags.forEach(tag => {
      if (tag.alarm_priority) {
        const priority: AlarmPriority = {
          ...tag.alarm_priority,
          description: tag.alarm_priority.description || `Priority ${tag.alarm_priority.level} alarm`
        };
        priorities.set(tag.alarm_priority.level, priority);
      }
    });
    return Array.from(priorities.values()).sort((a, b) => a.level - b.level);
  }, [tags]);

  // Update column visibility based on settings
  useEffect(() => {
    setColumns(prev => prev.map(col => {
      if (col.id === 'current_value_display') {
        return { ...col, visible: showValues };
      }
      if (col.id === 'description') {
        return { ...col, visible: showDescriptions };
      }
      if (col.id === 'alarm_status') {
        return { ...col, visible: showAlarmStatus };
      }
      return col;
    }));
  }, [showValues, showDescriptions, showAlarmStatus]);

  // Filter and sort data
  const filteredAndSortedTags = useMemo(() => {
    let filtered = tags.filter(tag => {
      // Text filter
      const matchesText = !filterText || 
        tag.tag_name.toLowerCase().includes(filterText.toLowerCase()) ||
        tag.description.toLowerCase().includes(filterText.toLowerCase()) ||
        (tag.equipment_name && tag.equipment_name.toLowerCase().includes(filterText.toLowerCase()));

      // Alarm status filter
      const matchesAlarmFilter = alarmFilter === 'all' ||
        (alarmFilter === 'alarms' && ['HIGH_ALARM', 'LOW_ALARM', 'BOOLEAN_TRUE_ALARM', 'BOOLEAN_FALSE_ALARM'].includes(tag.alarm_status)) ||
        (alarmFilter === 'normal' && ['NORMAL', 'ALARMS_DISABLED'].includes(tag.alarm_status));

      // Priority filter
      const matchesPriorityFilter = priorityFilter.length === 0 ||
        (tag.alarm_priority && priorityFilter.includes(tag.alarm_priority.level));

      return matchesText && matchesAlarmFilter && matchesPriorityFilter;
    });

    // Sort
    filtered.sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];
      
      if (aValue === undefined || aValue === null) return 1;
      if (bValue === undefined || bValue === null) return -1;
      
      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue);
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else {
        comparison = String(aValue).localeCompare(String(bValue));
      }
      
      return order === 'desc' ? -comparison : comparison;
    });

    return filtered;
  }, [tags, filterText, alarmFilter, priorityFilter, orderBy, order]);

  // Handlers
  const handleSort = (property: keyof SCADATag) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleColumnVisibilityChange = (columnId: string, visible: boolean) => {
    setColumns(prev => prev.map(col => 
      col.id === columnId ? { ...col, visible } : col
    ));
  };

  const handleExport = () => {
    // Basic CSV export
    const visibleColumns = columns.filter(col => col.visible && col.id !== 'actions');
    const headers = visibleColumns.map(col => col.label).join(',');
    const rows = filteredAndSortedTags.map(tag => {
      return visibleColumns.map(col => {
        if (col.id === 'alarm_status') {
          return `"${tag.alarm_status}"`;
        }
        const value = tag[col.id as keyof SCADATag];
        return `"${value || ''}"`;
      }).join(',');
    }).join('\n');
    
    const csv = headers + '\n' + rows;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scada_tags_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const visibleColumns = columns.filter(col => col.visible);

  return (
    <Box>
      {/* Configuration Panel */}
      {showConfiguration && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Table Configuration
            </Typography>
            
            <Grid container spacing={2} alignItems="center">
              {/* Quick toggles */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showValues}
                      onChange={(e) => setShowValues(e.target.checked)}
                    />
                  }
                  label="Show Values"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showDescriptions}
                      onChange={(e) => setShowDescriptions(e.target.checked)}
                    />
                  }
                  label="Show Descriptions"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showAlarmStatus}
                      onChange={(e) => setShowAlarmStatus(e.target.checked)}
                    />
                  }
                  label="Show Alarm Status"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    startIcon={<ExportIcon />}
                    onClick={handleExport}
                    size="small"
                    variant="outlined"
                  >
                    Export CSV
                  </Button>
                  {onRefresh && (
                    <IconButton onClick={onRefresh} size="small">
                      <RefreshIcon />
                    </IconButton>
                  )}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Search tags..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Alarm Filter</InputLabel>
                <Select
                  value={alarmFilter}
                  onChange={(e) => setAlarmFilter(e.target.value)}
                  label="Alarm Filter"
                >
                  <MenuItem value="all">All Tags</MenuItem>
                  <MenuItem value="alarms">Only Alarms</MenuItem>
                  <MenuItem value="normal">Normal Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Priority Filter</InputLabel>
                <Select
                  multiple
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value as number[])}
                  input={<OutlinedInput label="Priority Filter" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((level) => {
                        const priority = availablePriorities.find(p => p.level === level);
                        return (
                          <Chip
                            key={level}
                            label={priority?.name || `Level ${level}`}
                            size="small"
                            sx={{
                              backgroundColor: priority?.color || '#1976d2',
                              color: 'white',
                            }}
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {availablePriorities.map((priority) => (
                    <MenuItem key={priority.level} value={priority.level}>
                      <Checkbox checked={priorityFilter.includes(priority.level)} />
                      <Chip
                        label={priority.name}
                        size="small"
                        sx={{
                          backgroundColor: priority.color,
                          color: 'white',
                          ml: 1,
                        }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Results Summary */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredAndSortedTags.length} of {tags.length} tags
        </Typography>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {visibleColumns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || 'left'}
                  style={{ width: column.width }}
                  sortDirection={orderBy === column.id ? order : false}
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleSort(column.id as keyof SCADATag)}
                    >
                      {column.label}
                      {orderBy === column.id ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedTags.map((tag) => (
              <TableRow
                key={tag.id}
                hover
                sx={{ cursor: onTagClick ? 'pointer' : 'default' }}
                onClick={() => onTagClick?.(tag)}
              >
                {visibleColumns.map((column) => (
                  <TableCell key={column.id} align={column.align || 'left'}>
                    {column.id === 'tag_name' && (
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {tag.tag_name}
                      </Typography>
                    )}
                    {column.id === 'description' && (
                      <Typography variant="body2" color="text.secondary">
                        {tag.description}
                      </Typography>
                    )}
                    {column.id === 'current_value_display' && (
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {tag.current_value_display}
                      </Typography>
                    )}
                    {column.id === 'alarm_status' && (
                      getAlarmStatusChip(tag.alarm_status, tag.alarm_priority ? {
                        ...tag.alarm_priority,
                        description: tag.alarm_priority.description || `Priority ${tag.alarm_priority.level} alarm`
                      } : undefined)
                    )}
                    {column.id === 'equipment_name' && (
                      <Typography variant="body2">
                        {tag.equipment_name || '-'}
                      </Typography>
                    )}
                    {column.id === 'timestamp' && (
                      <Typography variant="body2" color="text.secondary">
                        {tag.timestamp ? new Date(tag.timestamp).toLocaleString() : '-'}
                      </Typography>
                    )}
                    {column.id === 'actions' && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onTagClick?.(tag);
                        }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredAndSortedTags.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No tags found matching the current filters.
        </Alert>
      )}
    </Box>
  );
};

